import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import {
  getActiveUsersData,
  getAppOpenDayData,
  getDailySessionsPerDAU,
  getDeviceBreakdown,
  getGeoActiveUsersData,
  getSignUpDayData,
  getStickinessData,
  getTotalStats,
  getUserRetention,
} from "../../api/new-api";
import { DayData } from "../../types";
import { Point, toPoints } from "./analytics-utils";

type DayFetcher = (
  args: { dayCount: number },
  signal?: AbortSignal
) => Promise<{ data: { dayData?: DayData[] } }>;

export type SeriesKey =
  | "signups"
  | "appOpens"
  | "activeUsers"
  | "geoActiveUsers"
  | "sessionsPerUser"
  | "stickiness";

const SERIES: Record<SeriesKey, DayFetcher> = {
  signups: getSignUpDayData,
  appOpens: getAppOpenDayData,
  activeUsers: getActiveUsersData,
  geoActiveUsers: getGeoActiveUsersData,
  sessionsPerUser: getDailySessionsPerDAU,
  stickiness: getStickinessData,
};

const STALE_TIME = 5 * 60 * 1000;

/**
 * Asks for twice the range so the page can compare against the previous period. If the API
 * refuses the longer window, falls back to the range alone and the page shows no comparison.
 */
async function fetchSeries(
  key: SeriesKey,
  range: number,
  signal: AbortSignal
): Promise<Point[]> {
  const fetcher = SERIES[key];
  try {
    return toPoints((await fetcher({ dayCount: range * 2 }, signal)).data.dayData);
  } catch (err) {
    if (axios.isCancel(err) || signal.aborted) throw err;
    return toPoints((await fetcher({ dayCount: range }, signal)).data.dayData);
  }
}

export function useDaySeries(
  key: SeriesKey,
  venueId: number | undefined,
  range: number
) {
  return useQuery({
    queryKey: ["analytics", key, venueId, range],
    queryFn: ({ signal }) => fetchSeries(key, range, signal),
    enabled: venueId !== undefined,
    staleTime: STALE_TIME,
    retry: 1,
    // Keep the chart on screen while another range loads, but never show one venue's
    // figures under another's name.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === venueId ? previous : undefined,
  });
}

export function useTotalStats(venueId: number | undefined) {
  return useQuery({
    queryKey: ["analytics", "total-stats", venueId],
    queryFn: async ({ signal }) => (await getTotalStats(signal)).data,
    enabled: venueId !== undefined,
    staleTime: STALE_TIME,
    retry: 1,
  });
}

export function useDeviceBreakdown(venueId: number | undefined) {
  return useQuery({
    queryKey: ["analytics", "device-breakdown", venueId],
    queryFn: async ({ signal }) => (await getDeviceBreakdown(signal)).data,
    enabled: venueId !== undefined,
    staleTime: STALE_TIME,
    retry: 1,
  });
}

export function useRetention(venueId: number | undefined) {
  return useQuery({
    queryKey: ["analytics", "retention", venueId],
    queryFn: async () => (await getUserRetention()).data.lastThirtyDayData ?? [],
    enabled: venueId !== undefined,
    staleTime: STALE_TIME,
    retry: 1,
  });
}
